// frontend/src/pages/Contact.jsx
import React, { useEffect, useState } from "react";
import feather from "feather-icons";
import { ethers } from "ethers";

const Contact = () => {
  const [wallet, setWallet] = useState(localStorage.getItem("wallet-address"));
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  // === Initialisation Feather + vérif wallet ===
  useEffect(() => {
    feather.replace();
    if (window.ethereum) {
      window.ethereum
        .request({ method: "eth_accounts" })
        .then((accounts) => {
          if (accounts.length > 0) setWallet(accounts[0]);
        })
        .catch(console.error);
    }

    return () => feather.replace(); // nettoyage propre
  }, []);

  // === Connexion Wallet ===
  const connectWallet = async () => {
    if (!window.ethereum) return alert("Please install MetaMask!");
    try {
      const provider = new ethers.BrowserProvider(window.ethereum);
      await provider.send("eth_requestAccounts", []);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      localStorage.setItem("wallet-address", address);
      setWallet(address);
    } catch (err) {
      console.error("Wallet connection error:", err);
      setError("Wallet connection failed. Try again.");
    }
  };

  // === Formulaire ===
  const handleChange = (e) =>
    setFormData((prev) => ({ ...prev, [e.target.id]: e.target.value }));

  const handleSendMessage = async (e) => {
    e.preventDefault();
    setError("");
    const message = formData.message.trim();
    if (!message) return setError("Veuillez saisir un message.");

    setSending(true);
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          to: "contact@nexora.locker",
          name: formData.name || "—",
          email: formData.email || "—",
          message,
        }),
      });
      if (!res.ok) throw new Error(await res.text());

      setSent(true);
      setFormData({ name: "", email: "", message: "" });
      setTimeout(() => setSent(false), 4000);
    } catch (err) {
      console.error("send message error", err);
      setError("Échec de l'envoi. Vérifie ta connexion.");
    } finally {
      setSending(false);
    }
  };

  const shortAddress = wallet ? `${wallet.slice(0, 6)}...${wallet.slice(-4)}` : null;

  return (
    <div className="text-white py-20 min-h-screen">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* === Header === */}
        <div className="text-center mb-16">
          <h1 className="text-5xl font-extrabold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-neonPink to-neonBlue">
            Contact Us
          </h1>
          <p className="text-gray-300 text-lg">
            Have a question or collaboration idea? Reach out to the NEXORA team.
          </p>
        </div>

        {/* === Grid principale === */}
        <div className="grid md:grid-cols-2 gap-12">
          {/* === Formulaire === */}
          <div className="bg-gray-900/80 backdrop-blur rounded-2xl p-8 border border-neonPurple/40 hover:border-neonPink/50 transition-all shadow-lg">
            <h2 className="text-2xl font-bold mb-6 text-neonBlue flex items-center gap-2">
              <i data-feather="mail" className="w-5 h-5" />
              Send us a Message
            </h2>

            <form onSubmit={handleSendMessage} className="space-y-6">
              {error && (
                <p className="text-red-400 bg-red-900/20 rounded-lg px-3 py-2 text-sm">
                  ⚠️ {error}
                </p>
              )}
              {wallet && (
                <div className="text-sm text-gray-400 mb-2">
                  Connected:{" "}
                  <span className="text-neonPink font-semibold">{shortAddress}</span>
                </div>
              )}
              {!wallet && (
                <button
                  type="button"
                  onClick={connectWallet}
                  className="w-full mb-4 py-2 bg-gradient-to-r from-neonPurple to-neonPink rounded-lg font-semibold hover:opacity-90 transition"
                >
                  Connect Wallet
                </button>
              )}

              <div>
                <label className="block text-sm font-medium mb-2">Your Name</label>
                <input
                  id="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800 border border-neonPurple/30 rounded-lg px-4 py-3 focus:ring-2 focus:ring-neonPurple outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Email</label>
                <input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800 border border-neonPurple/30 rounded-lg px-4 py-3 focus:ring-2 focus:ring-neonPurple outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Message</label>
                <textarea
                  id="message"
                  rows="5"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="w-full bg-gray-800 border border-neonPurple/30 rounded-lg px-4 py-3 focus:ring-2 focus:ring-neonPurple outline-none resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={sending}
                className="w-full py-3 bg-gradient-to-r from-neonPurple to-neonPink rounded-lg font-bold text-lg hover:opacity-90 transition disabled:opacity-50"
              >
                {sending ? "Sending..." : "Send Message"}
              </button>

              {sent && (
                <div className="text-center text-green-400 font-medium mt-4 animate-fadeIn">
                  ✅ Message sent successfully!
                </div>
              )}
            </form>
          </div>

          {/* === Informations de contact === */}
          <div className="bg-gray-900/80 backdrop-blur rounded-2xl p-8 border border-neonBlue/40 shadow-lg hover:border-neonPink/40 transition-all">
            <h2 className="text-2xl font-bold mb-6 text-neonPink flex items-center gap-2">
              <i data-feather="info" className="w-5 h-5" />
              Contact Information
            </h2>
            <div className="space-y-6">
              {[
                {
                  icon: "mail",
                  label: "Email",
                  value: "contact@nexora.locker",
                  color: "text-neonPink",
                },
                {
                  icon: "globe",
                  label: "Website",
                  value: "www.nexora.locker",
                  color: "text-neonBlue",
                },
                {
                  icon: "twitter",
                  label: "Twitter",
                  value: "@nexoralocker",
                  color: "text-neonPurple",
                },
                {
                  icon: "message-circle",
                  label: "Telegram",
                  value: "t.me/NXOCommunity",
                  color: "text-neonPink",
                },
              ].map(({ icon, label, value, color }) => (
                <div key={label} className="flex items-start gap-4">
                  <div className={color}>
                    <i data-feather={icon} className="w-6 h-6" />
                  </div>
                  <div>
                    <h3 className="font-bold">{label}</h3>
                    <p className="text-gray-400">{value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
